import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Chip,
  TextField, Select, MenuItem, CircularProgress,
  Tooltip, Stack, ToggleButton, ToggleButtonGroup, InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import apiClient from '../api/client';

const CATEGORIES = [
  'Tools/Software',
  'Sales & Marketing',
  'Travel',
  'Administrative Expenses',
  'Insurance',
  'Founder Draws',
  'Sourcing/Vetting',
  'FX & Payment Fees',
];

const SUBCATEGORIES = [
  'M365', 'Ahrefs', 'Supabase/Vercel', 'Claude/Gemini API',
  'LinkedIn Sales Navigator/Apollo', 'Cal.com/Calendly', 'Outbound Email Vendor',
  'Content/GEO Spend', 'Legal', 'Accounting', 'Microsoft AI Cloud Partner Program',
  'Davis Draw', 'Sahdev Draw', 'Recruiting/Vetting Fees', 'Wire Transfer Fees',
  'FX Conversion Loss', 'Other',
];

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const CATEGORY_STYLE: Record<string, { bg: string; color: string }> = {
  'Tools/Software':          { bg: '#EFF6FF', color: '#3B82F6' },
  'Sales & Marketing':       { bg: '#FFF7ED', color: '#EA580C' },
  'Travel':                  { bg: '#F0FDF4', color: '#16A34A' },
  'Administrative Expenses': { bg: '#F8FAFC', color: '#475569' },
  'Insurance':               { bg: '#FDF4FF', color: '#9333EA' },
  'Founder Draws':           { bg: '#FFF1F2', color: '#E11D48' },
  'Sourcing/Vetting':        { bg: '#ECFDF5', color: '#059669' },
  'FX & Payment Fees':       { bg: '#FFFBEB', color: '#D97706' },
};

interface Expense {
  id: string;
  expense_date: string;
  month: number;
  year: number;
  category: string;
  subcategory: string | null;
  vendor_description: string | null;
  amount_usd: number;
  amount_inr: number | null;
  notes: string | null;
}

interface EditForm {
  expense_date: string;
  category: string;
  subcategory: string;
  vendor_description: string;
  amount_usd: string;
  amount_inr: string;
  notes: string;
}

const emptyForm: EditForm = {
  expense_date: '',
  category: '',
  subcategory: '',
  vendor_description: '',
  amount_usd: '',
  amount_inr: '',
  notes: '',
};

const fmtUSD = (v: number) =>
  `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtINR = (v: number) =>
  `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function InlineSelect({ value, onChange, options, placeholder = '' }: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder?: string;
}) {
  return (
    <Select
      value={value}
      onChange={e => onChange(e.target.value)}
      size="small"
      displayEmpty
      sx={{
        fontSize: '0.8125rem',
        width: '100%',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#93C5FD' },
        '& .MuiSelect-select': { py: '5px', px: 1 },
      }}
    >
      <MenuItem value=""><em style={{ color: '#94A3B8', fontStyle: 'normal' }}>{placeholder || '—'}</em></MenuItem>
      {options.map(o => (
        <MenuItem key={o} value={o} sx={{ fontSize: '0.8125rem' }}>{o}</MenuItem>
      ))}
    </Select>
  );
}

function InlineField({ value, onChange, type = 'text', placeholder = '', startAdornment }: {
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  startAdornment?: React.ReactNode;
}) {
  return (
    <TextField
      value={value}
      onChange={e => onChange(e.target.value)}
      type={type}
      placeholder={placeholder}
      size="small"
      fullWidth
      InputProps={
        startAdornment
          ? { startAdornment: <InputAdornment position="start" sx={{ mr: 0.25 }}>{startAdornment}</InputAdornment> }
          : undefined
      }
      sx={{
        '& .MuiOutlinedInput-root': {
          fontSize: '0.8125rem',
          '& fieldset': { borderColor: '#93C5FD' },
        },
        '& input': { py: '5px', px: 1 },
      }}
    />
  );
}

function DateInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <input
      type="date"
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        fontSize: '0.8125rem',
        border: '1px solid #93C5FD',
        borderRadius: 6,
        padding: '5px 6px',
        outline: 'none',
        width: '100%',
        fontFamily: 'inherit',
        color: '#0F172A',
        backgroundColor: '#fff',
      }}
    />
  );
}

const DASH = <span style={{ color: '#CBD5E1' }}>—</span>;

export default function AdminExpensesPage() {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(dayjs().year());
  const [monthFilter, setMonthFilter] = useState(0);
  const [categoryFilter, setCategoryFilter] = useState('');

  const [addingRow, setAddingRow] = useState(false);
  const [newForm, setNewForm] = useState<EditForm>(emptyForm);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditForm>(emptyForm);

  const [saving, setSaving] = useState(false);

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = { year: String(year) };
      if (monthFilter > 0) params.month = String(monthFilter);
      if (categoryFilter) params.category = categoryFilter;
      const res = await apiClient.get('/expenses', { params });
      setExpenses(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [year, monthFilter, categoryFilter]);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

  const handleAddClick = () => {
    setEditingId(null);
    setNewForm({ ...emptyForm, expense_date: dayjs().format('YYYY-MM-DD') });
    setAddingRow(true);
  };

  const handleSaveNew = async () => {
    if (!newForm.expense_date || !newForm.category || !newForm.amount_usd) return;
    setSaving(true);
    try {
      await apiClient.post('/expenses', {
        expense_date: newForm.expense_date,
        category: newForm.category,
        subcategory: newForm.subcategory || null,
        vendor_description: newForm.vendor_description || null,
        amount_usd: parseFloat(newForm.amount_usd),
        amount_inr: newForm.amount_inr ? parseFloat(newForm.amount_inr) : null,
        notes: newForm.notes || null,
      });
      setAddingRow(false);
      setNewForm(emptyForm);
      // Jump to the month of the saved expense so the user sees it immediately
      const parts = newForm.expense_date.split('-');
      setYear(parseInt(parts[0], 10));
      setMonthFilter(parseInt(parts[1], 10));
      // fetchExpenses fires via useEffect when year/monthFilter change
    } finally {
      setSaving(false);
    }
  };

  const handleStartEdit = (exp: Expense) => {
    setAddingRow(false);
    setEditingId(exp.id);
    setEditForm({
      expense_date: exp.expense_date.substring(0, 10),
      category: exp.category,
      subcategory: exp.subcategory || '',
      vendor_description: exp.vendor_description || '',
      amount_usd: String(exp.amount_usd),
      amount_inr: exp.amount_inr != null ? String(exp.amount_inr) : '',
      notes: exp.notes || '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      await apiClient.put(`/expenses/${editingId}`, {
        expense_date: editForm.expense_date,
        category: editForm.category,
        subcategory: editForm.subcategory || null,
        vendor_description: editForm.vendor_description || null,
        amount_usd: parseFloat(editForm.amount_usd),
        amount_inr: editForm.amount_inr ? parseFloat(editForm.amount_inr) : null,
        notes: editForm.notes || null,
      });
      setEditingId(null);
      fetchExpenses();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await apiClient.delete(`/expenses/${id}`);
      setExpenses(prev => prev.filter(e => e.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  const totalUSD = expenses.reduce((sum, e) => sum + Number(e.amount_usd), 0);
  const totalINR = expenses.reduce((sum, e) => sum + Number(e.amount_inr || 0), 0);

  const cellSx = { py: 0.75, px: 1.25, fontSize: '0.8125rem', verticalAlign: 'middle' };

  const editingRowBg = {
    backgroundColor: '#F0F9FF',
    '& td': { borderBottom: '2px solid #BFDBFE !important' },
  };

  const actionButtons = (onSave: () => void, onCancel: () => void) => (
    <Stack direction="row" spacing={0.5} justifyContent="center">
      <Tooltip title="Save">
        <span>
          <IconButton size="small" color="primary" onClick={onSave} disabled={saving}>
            <CheckIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Cancel">
        <IconButton size="small" onClick={onCancel} sx={{ color: '#94A3B8' }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  const monthLabelFrom = (dateStr: string) =>
    dateStr ? MONTH_ABBR[parseInt(dateStr.split('-')[1], 10) - 1] : '—';

  const editingRow = (form: EditForm, onSave: () => void, onCancel: () => void, key: string) => (
    <TableRow key={key} sx={editingRowBg}>
      <TableCell sx={cellSx}>
        <DateInput value={form.expense_date} onChange={v => {
          if (key === 'new-row') setNewForm(f => ({ ...f, expense_date: v }));
          else setEditForm(f => ({ ...f, expense_date: v }));
        }} />
      </TableCell>
      <TableCell sx={{ ...cellSx, color: '#94A3B8', fontSize: '0.75rem' }}>
        {monthLabelFrom(form.expense_date)}
      </TableCell>
      <TableCell sx={cellSx}>
        <InlineSelect
          value={form.category}
          onChange={v => key === 'new-row' ? setNewForm(f => ({ ...f, category: v })) : setEditForm(f => ({ ...f, category: v }))}
          options={CATEGORIES}
          placeholder="Select"
        />
      </TableCell>
      <TableCell sx={cellSx}>
        <InlineSelect
          value={form.subcategory}
          onChange={v => key === 'new-row' ? setNewForm(f => ({ ...f, subcategory: v })) : setEditForm(f => ({ ...f, subcategory: v }))}
          options={SUBCATEGORIES}
          placeholder="Select"
        />
      </TableCell>
      <TableCell sx={cellSx}>
        <InlineField
          value={form.vendor_description}
          onChange={v => key === 'new-row' ? setNewForm(f => ({ ...f, vendor_description: v })) : setEditForm(f => ({ ...f, vendor_description: v }))}
          placeholder="Vendor / Description"
        />
      </TableCell>
      <TableCell sx={cellSx} align="right">
        <InlineField
          value={form.amount_usd}
          onChange={v => key === 'new-row' ? setNewForm(f => ({ ...f, amount_usd: v })) : setEditForm(f => ({ ...f, amount_usd: v }))}
          type="number"
          placeholder="0.00"
          startAdornment="$"
        />
      </TableCell>
      <TableCell sx={cellSx} align="right">
        <InlineField
          value={form.amount_inr}
          onChange={v => key === 'new-row' ? setNewForm(f => ({ ...f, amount_inr: v })) : setEditForm(f => ({ ...f, amount_inr: v }))}
          type="number"
          placeholder="0.00"
          startAdornment="₹"
        />
      </TableCell>
      <TableCell sx={cellSx}>
        <InlineField
          value={form.notes}
          onChange={v => key === 'new-row' ? setNewForm(f => ({ ...f, notes: v })) : setEditForm(f => ({ ...f, notes: v }))}
          placeholder="Notes"
        />
      </TableCell>
      <TableCell sx={cellSx} align="center">
        {actionButtons(onSave, onCancel)}
      </TableCell>
    </TableRow>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Page header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5">Expenses</Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
            Operational expense tracker — Tools, Payroll, Travel, and more
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          disabled={addingRow}
          sx={{ borderRadius: 2, fontWeight: 600, px: 2.5 }}
        >
          Add Expense
        </Button>
      </Box>

      {/* Filter bar */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" gap={1}>
          {/* Year navigation */}
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <IconButton size="small" onClick={() => setYear(y => y - 1)}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography variant="subtitle2" sx={{ minWidth: 38, textAlign: 'center', userSelect: 'none' }}>
              {year}
            </Typography>
            <IconButton size="small" onClick={() => setYear(y => y + 1)}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Stack>

          {/* Month filter */}
          <ToggleButtonGroup
            value={monthFilter}
            exclusive
            onChange={(_, v) => { if (v !== null) setMonthFilter(v); }}
            size="small"
          >
            <ToggleButton value={0} sx={{ px: 1.25, minWidth: 36, fontSize: '0.7rem', fontWeight: 500 }}>
              All
            </ToggleButton>
            {MONTH_ABBR.map((m, i) => (
              <ToggleButton key={m} value={i + 1} sx={{ px: 0.75, minWidth: 30, fontSize: '0.7rem', fontWeight: 500 }}>
                {m}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {/* Category filter */}
          <Select
            value={categoryFilter}
            onChange={e => setCategoryFilter(e.target.value)}
            displayEmpty
            size="small"
            sx={{ fontSize: '0.8125rem', minWidth: 170 }}
          >
            <MenuItem value=""><em>All Categories</em></MenuItem>
            {CATEGORIES.map(c => (
              <MenuItem key={c} value={c} sx={{ fontSize: '0.8125rem' }}>{c}</MenuItem>
            ))}
          </Select>

          {/* Totals */}
          <Stack direction="row" spacing={2.5} sx={{ ml: 'auto' }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                {expenses.length} {expenses.length === 1 ? 'entry' : 'entries'} · USD
              </Typography>
              <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' }}>
                {fmtUSD(totalUSD)}
              </Typography>
            </Box>
            {totalINR > 0 && (
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  INR
                </Typography>
                <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em', color: '#059669' }}>
                  {fmtINR(totalINR)}
                </Typography>
              </Box>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* Spreadsheet table */}
      <Paper variant="outlined">
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 1250, tableLayout: 'fixed' }} size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '108px' }}>Date</TableCell>
                <TableCell sx={{ width: '50px' }}>Month</TableCell>
                <TableCell sx={{ width: '160px' }}>Category</TableCell>
                <TableCell sx={{ width: '165px' }}>Subcategory</TableCell>
                <TableCell sx={{ width: '185px' }}>Vendor / Description</TableCell>
                <TableCell align="right" sx={{ width: '110px' }}>Amount (USD)</TableCell>
                <TableCell align="right" sx={{ width: '110px' }}>Amount (INR)</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="center" sx={{ width: '76px' }}>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {/* New row */}
              {addingRow && editingRow(
                newForm,
                handleSaveNew,
                () => setAddingRow(false),
                'new-row',
              )}

              {/* Loading */}
              {loading && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              )}

              {/* Empty state */}
              {!loading && expenses.length === 0 && !addingRow && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6, color: '#94A3B8' }}>
                    No expenses found. Click "+ Add Expense" to get started.
                  </TableCell>
                </TableRow>
              )}

              {/* Data rows */}
              {!loading && expenses.map(exp => {
                if (editingId === exp.id) {
                  return editingRow(editForm, handleSaveEdit, () => setEditingId(null), exp.id);
                }

                const catStyle = CATEGORY_STYLE[exp.category] || { bg: '#F1F5F9', color: '#64748B' };

                return (
                  <TableRow key={exp.id}>
                    <TableCell sx={cellSx}>
                      {dayjs(exp.expense_date.substring(0, 10)).format('DD MMM YYYY')}
                    </TableCell>
                    <TableCell sx={{ ...cellSx, color: '#64748B' }}>
                      {MONTH_ABBR[exp.month - 1]}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Chip
                        label={exp.category}
                        size="small"
                        sx={{
                          bgcolor: catStyle.bg,
                          color: catStyle.color,
                          fontWeight: 600,
                          fontSize: '0.68rem',
                          height: 20,
                          border: 'none',
                          maxWidth: '100%',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...cellSx, color: '#334155' }}>
                      {exp.subcategory || DASH}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Typography
                        sx={{ fontSize: '0.8125rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: exp.vendor_description ? '#334155' : '#CBD5E1' }}
                        title={exp.vendor_description || ''}
                      >
                        {exp.vendor_description || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={cellSx} align="right">
                      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: '#0F172A' }}>
                        {fmtUSD(exp.amount_usd)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={cellSx} align="right">
                      {exp.amount_inr != null ? (
                        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: '#059669' }}>
                          {fmtINR(exp.amount_inr)}
                        </Typography>
                      ) : DASH}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <Typography
                        sx={{ fontSize: '0.8125rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: exp.notes ? '#64748B' : '#CBD5E1' }}
                        title={exp.notes || ''}
                      >
                        {exp.notes || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={cellSx} align="center">
                      <Stack direction="row" spacing={0.25} justifyContent="center">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleStartEdit(exp)} sx={{ color: '#94A3B8', '&:hover': { color: '#3B82F6' } }}>
                            <EditIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDelete(exp.id)} sx={{ color: '#94A3B8', '&:hover': { color: '#EF4444' } }}>
                            <DeleteIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
